import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  CalendarRange, FileSpreadsheet, ChevronRight, ChevronLeft, Send,
  MessageSquareWarning, CheckCircle2, Clock, Eye, Pencil, Save, X,
  ArrowLeft, FileText, User as UserIcon, ChevronDown, ChevronUp, Trash2
} from 'lucide-react';

const STATUS_META = {
  Draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  Forwarded_AH: { label: 'Forwarded to Head', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  Forwarded_Principal: { label: 'Forwarded to Principal', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  Forwarded_CEO: { label: 'Forwarded to CEO', color: 'bg-violet-50 text-violet-700 border-violet-200' },
  Clarification_CIC: { label: 'Clarification: CIC', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  Clarification_AH: { label: 'Clarification: Head', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  Clarification_Principal: { label: 'Clarification: Principal', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  Reviewed: { label: 'Reviewed', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, color: 'bg-slate-100 text-slate-600 border-slate-200' };
  return (
    <span data-testid={`status-${status}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${meta.color}`}>
      {meta.label}
    </span>
  );
}

// Get Monday-Sunday for a given date
function getWeekRange(d) {
  const date = new Date(d);
  const day = date.getDay(); // 0 Sun, 1 Mon
  const diffToMon = day === 0 ? -6 : 1 - day;
  const mon = new Date(date); mon.setDate(date.getDate() + diffToMon);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const fmt = (dt) => dt.toISOString().split('T')[0];
  return { from: fmt(mon), to: fmt(sun) };
}

export default function WeeklySummaryPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState('list'); // list | detail
  const [selectedId, setSelectedId] = useState(null);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('faculty'); // 'faculty' | 'cic'

  // Open via ?open=<id> deep link
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      setSelectedId(openId);
      setView('detail');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Generate form
  const today = new Date();
  const defaultWeek = getWeekRange(today);
  const [weekFrom, setWeekFrom] = useState(defaultWeek.from);
  const [weekTo, setWeekTo] = useState(defaultWeek.to);
  const [generating, setGenerating] = useState(false);

  const loadSummaries = async (st) => {
    setLoading(true);
    try {
      const { data } = await api.getWeeklySummaries(st);
      setSummaries(data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load summaries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSummaries(); }, []);

  const handleGenerate = async (st = 'faculty') => {
    if (!weekFrom || !weekTo) return;
    setError('');
    setGenerating(true);
    try {
      const { data } = await api.generateWeeklySummary(weekFrom, weekTo, st);
      await loadSummaries();
      setSelectedId(data._id);
      setView('detail');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to generate summary');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Delete this weekly summary? This cannot be undone.')) return;
    try {
      await api.deleteWeeklySummary(id);
      await loadSummaries();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const openDetail = (id) => { setSelectedId(id); setView('detail'); };
  const backToList = () => { setSelectedId(null); setView('list'); loadSummaries(); };

  if (view === 'detail' && selectedId) {
    return <WeeklySummaryDetail id={selectedId} onBack={backToList} user={user} />;
  }

  const isCIC = user?.role === 'CIC';
  const isHead = user?.role === 'AcademicHead';
  const isPrincipal = user?.role === 'PrincipalSSV';
  const showTabs = ['AcademicHead', 'PrincipalSSV', 'CEO', 'SuperAdmin'].includes(user?.role);
  const tabDefs = [
    { key: 'faculty', label: 'Faculty Summaries (by CIC)', color: 'text-blue-700 border-blue-600' },
    { key: 'cic', label: 'CIC Summaries (by Head)', color: 'text-indigo-700 border-indigo-600' },
    ...(['AcademicHead', 'PrincipalSSV', 'CEO', 'SuperAdmin'].includes(user?.role)
      ? [{ key: 'head', label: user?.role === 'AcademicHead' ? 'Head Summaries (by Head)' : 'Head Summaries (by Principal)', color: 'text-violet-700 border-violet-600' }]
      : []),
  ];
  const filteredSummaries = showTabs
    ? summaries.filter(s => (s.summary_type || 'faculty') === activeTab)
    : summaries;

  return (
    <div data-testid="weekly-summary-page" className="space-y-6 animate-fade-in max-w-[1400px]">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">{isHead ? 'Head Workflow' : isPrincipal ? 'Principal Workflow' : 'CIC Workflow'}</p>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Weekly Summary Reports
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {isCIC && 'Generate, edit and forward weekly faculty summaries to Head.'}
          {isHead && 'Generate weekly CIC summaries and forward faculty/CIC summaries to Principal. Mark critical items before forwarding.'}
          {isPrincipal && 'Generate the weekly Head summary, review escalated CIC/Faculty summaries, and forward everything to the CEO with comments.'}
          {!isCIC && !isHead && !isPrincipal && 'Review weekly summaries forwarded up the hierarchy.'}
        </p>
      </div>

      {error && (
        <div data-testid="weekly-summary-error" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {(isCIC || isHead || isPrincipal) && (
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600 flex items-center gap-2">
              <CalendarRange className="w-4 h-4 text-blue-600" /> Generate New Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <Label className="text-xs text-slate-500">Week From</Label>
                <Input data-testid="week-from-input" type="date" value={weekFrom} onChange={e => setWeekFrom(e.target.value)} className="border-slate-300" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Week To</Label>
                <Input data-testid="week-to-input" type="date" value={weekTo} onChange={e => setWeekTo(e.target.value)} className="border-slate-300" />
              </div>
              {isCIC && (
                <Button data-testid="generate-summary-btn" onClick={() => handleGenerate('faculty')} disabled={generating} className="bg-blue-600 text-white hover:bg-blue-700">
                  <FileSpreadsheet className="w-4 h-4 mr-1.5" /> {generating ? 'Generating...' : 'Generate Faculty Summary'}
                </Button>
              )}
              {isHead && (
                <>
                  <Button data-testid="generate-cic-summary-btn" onClick={() => handleGenerate('cic')} disabled={generating} className="bg-indigo-600 text-white hover:bg-indigo-700">
                    <FileSpreadsheet className="w-4 h-4 mr-1.5" /> {generating ? 'Generating...' : 'Generate CIC Summary'}
                  </Button>
                  <Button data-testid="generate-head-own-summary-btn" onClick={() => handleGenerate('head')} disabled={generating} className="bg-violet-600 text-white hover:bg-violet-700">
                    <FileSpreadsheet className="w-4 h-4 mr-1.5" /> {generating ? 'Generating...' : 'Generate Head Summary'}
                  </Button>
                </>
              )}
              {isPrincipal && (
                <Button data-testid="generate-head-summary-btn" onClick={() => handleGenerate('head')} disabled={generating} className="bg-violet-600 text-white hover:bg-violet-700">
                  <FileSpreadsheet className="w-4 h-4 mr-1.5" /> {generating ? 'Generating...' : 'Generate Head Summary'}
                </Button>
              )}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              {isCIC && 'Generates consolidated submissions for all faculty assigned to you for the selected week.'}
              {isHead && 'Generates consolidated activity (own work + review stats) for all CICs assigned to you. Generate Head Summary combines your own work + CICs under you in one tabulated report (forward to Principal).'}
              {isPrincipal && 'Generates consolidated activity (own work + review stats) for all Heads — forward to CEO with comments.'}
            </p>
          </CardContent>
        </Card>
      )}

      {showTabs && (
        <div data-testid="summary-tabs" className="flex items-center gap-2 border-b border-slate-200 flex-wrap">
          {tabDefs.map(t => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === t.key ? t.color : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.label}
              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                {summaries.filter(s => (s.summary_type || 'faculty') === t.key).length}
              </span>
            </button>
          ))}
        </div>
      )}

      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600 flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-700" /> Summaries ({filteredSummaries.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-slate-400">Loading...</p>
          ) : filteredSummaries.length === 0 ? (
            <p data-testid="no-summaries-msg" className="text-sm text-slate-500 py-6 text-center">No weekly summaries yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="py-2 px-2">Week</th>
                    <th className="py-2 px-2">Type</th>
                    <th className="py-2 px-2">Generated By</th>
                    <th className="py-2 px-2">Entries</th>
                    <th className="py-2 px-2">Total Hours</th>
                    <th className="py-2 px-2">Status</th>
                    <th className="py-2 px-2">Updated</th>
                    <th className="py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSummaries.map((s) => {
                    const totalHours = (s.faculty_summaries || []).reduce((a, f) => a + (f.total_hours || 0), 0);
                    const stype = s.summary_type || 'faculty';
                    return (
                      <tr key={s._id} data-testid={`summary-row-${s._id}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2.5 px-2 font-semibold text-slate-700">{s.week_from} → {s.week_to}</td>
                        <td className="py-2.5 px-2">
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${stype === 'head' ? 'bg-violet-100 text-violet-700' : stype === 'cic' ? 'bg-indigo-100 text-indigo-700' : 'bg-blue-100 text-blue-700'}`}>
                            {stype === 'head' ? 'Head' : stype === 'cic' ? 'CIC' : 'Faculty'}
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-slate-600">
                          <div className="leading-tight">
                            <div>{s.cic_name}</div>
                            {(s.cic_courses || []).length > 0 && (
                              <div className="text-[10px] text-slate-400">{s.cic_courses.join(', ')}</div>
                            )}
                          </div>
                        </td>
                        <td className="py-2.5 px-2 text-slate-600">{(s.faculty_summaries || []).length}</td>
                        <td className="py-2.5 px-2 text-slate-600">{totalHours.toFixed(1)}</td>
                        <td className="py-2.5 px-2"><StatusBadge status={s.status} /></td>
                        <td className="py-2.5 px-2 text-slate-400 text-xs">{s.updated_at ? s.updated_at.split('T')[0] : '-'}</td>
                        <td className="py-2.5 px-2">
                          <div className="flex items-center gap-1">
                            <Button data-testid={`open-summary-${s._id}`} onClick={() => openDetail(s._id)} variant="ghost" className="h-7 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                              <Eye className="w-3.5 h-3.5 mr-1" /> View
                            </Button>
                            {((user?.role === 'CIC' && s.cic_id === (user?._id || user?.id) && ['Draft', 'Clarification_CIC'].includes(s.status)) || (user?.role === 'AcademicHead' && stype === 'cic' && s.cic_id === (user?._id || user?.id) && ['Draft', 'Clarification_AH'].includes(s.status)) || user?.role === 'SuperAdmin') && (
                              <Button data-testid={`delete-summary-${s._id}`} onClick={(e) => handleDelete(s._id, e)} variant="ghost" className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============ DETAIL VIEW ============
function WeeklySummaryDetail({ id, onBack, user }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [observations, setObservations] = useState('');
  const [editedRows, setEditedRows] = useState([]);
  const [criticalOnly, setCriticalOnly] = useState(false);

  // Action modal state
  const [actionModal, setActionModal] = useState(null);
  const [actionComments, setActionComments] = useState('');
  const [actionTarget, setActionTarget] = useState('');

  const load = async (cOnly = criticalOnly) => {
    setLoading(true);
    try {
      const { data } = await api.getWeeklySummary(id, cOnly);
      setSummary(data);
      setObservations(data.cic_observations || '');
      setEditedRows(data.faculty_summaries || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [id]);

  const toggleCriticalOnly = async (val) => {
    setCriticalOnly(val);
    await load(val);
  };

  const userIdMatch = (user?._id || user?.id);
  const stype = summary?.summary_type || 'faculty';
  const isOwnerCIC = user?.role === 'CIC' && summary?.cic_id === userIdMatch;
  const isOwnerHead = user?.role === 'AcademicHead' && summary?.cic_id === userIdMatch && (stype === 'cic' || stype === 'head');
  const isOwnerPrincipal = user?.role === 'PrincipalSSV' && summary?.cic_id === userIdMatch && stype === 'head';
  const canEdit = (isOwnerCIC && ['Draft', 'Clarification_CIC'].includes(summary?.status))
    || (isOwnerHead && ['Draft', 'Clarification_AH'].includes(summary?.status))
    || (isOwnerPrincipal && ['Draft', 'Clarification_Principal'].includes(summary?.status))
    // Head can edit critical flags on faculty summaries forwarded to them
    || (user?.role === 'AcademicHead' && stype === 'faculty' && ['Forwarded_AH', 'Clarification_AH'].includes(summary?.status));
  const showCriticalToggle = ['PrincipalSSV', 'CEO', 'SuperAdmin'].includes(user?.role)
    && ['Forwarded_Principal', 'Clarification_Principal', 'Forwarded_CEO', 'Reviewed'].includes(summary?.status);

  const saveEdits = async () => {
    setSaving(true); setError('');
    try {
      await api.updateWeeklySummary(id, { cic_observations: observations, faculty_summaries: editedRows });
      setEditing(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const submitAction = async () => {
    if (!actionModal) return;
    setSaving(true); setError('');
    try {
      await api.weeklySummaryAction(id, actionModal.type, actionComments, actionTarget || undefined);
      setActionModal(null); setActionComments(''); setActionTarget('');
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to perform action');
    } finally {
      setSaving(false);
    }
  };

  const availableActions = useMemo(() => {
    if (!summary) return [];
    const role = user?.role;
    const status = summary.status;
    const uid = user?._id || user?.id;
    const t = summary.summary_type || 'faculty';
    const list = [];
    // CIC: forward own faculty summary to Head
    if (role === 'CIC' && t === 'faculty' && summary.cic_id === uid && ['Draft', 'Clarification_CIC'].includes(status)) {
      list.push({ type: 'forward', label: 'Forward to Head', color: 'bg-blue-600', icon: Send });
    }
    // Head: forward own CIC summary to Principal
    if (role === 'AcademicHead' && t === 'cic' && summary.cic_id === uid && ['Draft', 'Clarification_AH'].includes(status)) {
      list.push({ type: 'forward', label: 'Forward to Principal', color: 'bg-indigo-600', icon: Send });
    }
    // Head: forward own Head summary to Principal
    if (role === 'AcademicHead' && t === 'head' && summary.cic_id === uid && ['Draft', 'Clarification_AH'].includes(status)) {
      list.push({ type: 'forward', label: 'Forward to Principal', color: 'bg-violet-600', icon: Send });
    }
    // Head: forward incoming faculty summary to Principal
    if (role === 'AcademicHead' && t === 'faculty' && ['Forwarded_AH'].includes(status)) {
      list.push({ type: 'forward', label: 'Forward to Principal', color: 'bg-indigo-600', icon: Send });
      list.push({ type: 'clarification', label: 'Request Clarification (CIC)', color: 'bg-amber-500', icon: MessageSquareWarning });
    }
    if (role === 'PrincipalSSV' && ['Forwarded_Principal'].includes(status)) {
      list.push({ type: 'forward', label: 'Forward to CEO', color: 'bg-violet-600', icon: Send });
      list.push({ type: 'clarification', label: 'Request Clarification', color: 'bg-amber-500', icon: MessageSquareWarning, target: true });
    }
    // Principal: forward own Head summary to CEO
    if (role === 'PrincipalSSV' && t === 'head' && summary.cic_id === uid && ['Draft', 'Clarification_Principal'].includes(status)) {
      list.push({ type: 'forward', label: 'Forward to CEO', color: 'bg-violet-600', icon: Send });
    }
    if (['CEO', 'SuperAdmin'].includes(role) && ['Forwarded_CEO'].includes(status)) {
      list.push({ type: 'reviewed', label: 'Mark as Reviewed', color: 'bg-emerald-600', icon: CheckCircle2 });
      list.push({ type: 'clarification', label: 'Request Clarification', color: 'bg-amber-500', icon: MessageSquareWarning, target: true });
    }
    return list;
  }, [summary, user]);

  if (loading) return <div className="text-slate-400 text-sm">Loading...</div>;
  if (!summary) return <div className="text-red-500 text-sm">Summary not found.</div>;

  return (
    <div data-testid="weekly-summary-detail" className="space-y-6 animate-fade-in max-w-[1400px]">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button data-testid="back-to-list-btn" onClick={onBack} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 mb-2">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to list
          </button>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Weekly Summary</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {summary.week_from} → {summary.week_to}
          </h1>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${stype === 'head' ? 'bg-violet-100 text-violet-700' : stype === 'cic' ? 'bg-indigo-100 text-indigo-700' : 'bg-blue-100 text-blue-700'}`}>
              {stype === 'head' ? 'Head Summary' : stype === 'cic' ? 'CIC Summary' : 'Faculty Summary'}
            </span>
            <StatusBadge status={summary.status} />
            <span className="text-xs text-slate-500">by {summary.cic_name}</span>
            {(summary.cic_courses || []).length > 0 && (
              <span className="text-xs text-slate-500">· <span className="font-semibold">Courses:</span> {summary.cic_courses.join(', ')}</span>
            )}
            {summary._critical_only && (
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-red-100 text-red-700">Critical Only</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {showCriticalToggle && (
            <Button
              data-testid="critical-only-toggle"
              variant="outline"
              onClick={() => toggleCriticalOnly(!criticalOnly)}
              className={`border-slate-300 ${criticalOnly ? 'bg-red-50 text-red-700 border-red-300' : 'text-slate-700'}`}
            >
              {criticalOnly ? 'Show All' : 'Show Critical Only'}
            </Button>
          )}
          {canEdit && !editing && (
            <Button data-testid="edit-summary-btn" onClick={() => setEditing(true)} variant="outline" className="border-slate-300 text-slate-700">
              <Pencil className="w-4 h-4 mr-1.5" /> Edit
            </Button>
          )}
          {((isOwnerCIC && ['Draft', 'Clarification_CIC'].includes(summary.status)) || user?.role === 'SuperAdmin') && !editing && (
            <Button
              data-testid="delete-summary-btn"
              onClick={async () => {
                if (!window.confirm('Delete this weekly summary? This cannot be undone.')) return;
                try {
                  await api.deleteWeeklySummary(summary._id);
                  onBack();
                } catch (err) {
                  alert(err.response?.data?.detail || 'Failed to delete');
                }
              }}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete
            </Button>
          )}
          {editing && (
            <>
              <Button data-testid="cancel-edit-btn" onClick={() => { setEditing(false); setObservations(summary.cic_observations || ''); setEditedRows(summary.faculty_summaries || []); }} variant="ghost" className="text-slate-500">
                <X className="w-4 h-4 mr-1.5" /> Cancel
              </Button>
              <Button data-testid="save-edit-btn" onClick={saveEdits} disabled={saving} className="bg-slate-900 text-white hover:bg-slate-800">
                <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
          {!editing && availableActions.map((a) => {
            const Icon = a.icon;
            return (
              <Button
                key={a.type + a.label}
                data-testid={`action-${a.type}-btn`}
                onClick={() => { setActionModal(a); setActionComments(''); setActionTarget(''); }}
                className={`${a.color} text-white hover:opacity-90`}
              >
                <Icon className="w-4 h-4 mr-1.5" /> {a.label}
              </Button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}

      {/* Faculty/CIC entries - detailed expandable rows */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">
            {stype === 'cic' ? 'CIC Work Summary' : 'Faculty Work Summary'}
            {editing && (user?.role === 'AcademicHead' || stype === 'cic') && (
              <span className="ml-3 text-[10px] font-normal normal-case tracking-normal text-red-600">
                Tip: tick rows to mark them as critical — only critical items are forwarded upward.
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(editing ? editedRows : summary.faculty_summaries || []).map((f, i) => (
              <FacultyDetailCard
                key={f.faculty_id}
                index={i}
                faculty={f}
                editing={editing}
                onUpdate={(patch) => setEditedRows(prev => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r))}
              />
            ))}
            {(summary.faculty_summaries || []).length === 0 && (
              <p className="text-center text-slate-400 text-sm py-6">No faculty data</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* CIC Observations */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">CIC Observations</CardTitle>
        </CardHeader>
        <CardContent>
          {editing ? (
            <textarea
              data-testid="cic-observations-input"
              value={observations}
              onChange={e => setObservations(e.target.value)}
              rows={4}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Summary observations by CIC..."
            />
          ) : (
            <p className="text-sm text-slate-600 whitespace-pre-wrap">{summary.cic_observations || <span className="text-slate-400 italic">No observations added.</span>}</p>
          )}
        </CardContent>
      </Card>

      {/* Comments trail */}
      {(summary.comments_trail || []).length > 0 && (
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">Forwarding Trail</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {summary.comments_trail.map((c, i) => (
                <div key={i} data-testid={`trail-${i}`} className="border-l-2 border-slate-200 pl-3 py-1.5">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-semibold text-slate-700">{c.user_name}</span>
                    <span className="text-slate-400">({c.user_role})</span>
                    <span className="text-slate-400">•</span>
                    <span className="text-slate-500 uppercase font-semibold text-[10px] tracking-wider">{c.action}</span>
                    <span className="text-slate-400 ml-auto text-[10px]">{c.timestamp?.split('T')[0]}</span>
                  </div>
                  {c.comments && <p className="text-sm text-slate-600 mt-1">{c.comments}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Modal — rendered via portal to escape transform-based containing blocks */}
      {actionModal && createPortal(
        <div data-testid="action-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 100 }}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-slate-900">{actionModal.label}</h3>
              <button onClick={() => setActionModal(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
            {actionModal.target && (
              <div className="mb-3">
                <Label className="text-xs text-slate-500">Send clarification to</Label>
                <select
                  data-testid="clarification-target-select"
                  value={actionTarget}
                  onChange={e => setActionTarget(e.target.value)}
                  className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm"
                >
                  <option value="">Select role...</option>
                  <option value="CIC">CIC</option>
                  <option value="AcademicHead">Head</option>
                  {user?.role !== 'PrincipalSSV' && <option value="PrincipalSSV">Principal</option>}
                </select>
              </div>
            )}
            <Label className="text-xs text-slate-500">Comments {actionModal.type === 'clarification' ? '(required)' : '(optional)'}</Label>
            <textarea
              data-testid="action-comments-input"
              value={actionComments}
              onChange={e => setActionComments(e.target.value)}
              rows={3}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mt-1"
              placeholder="Add comments..."
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setActionModal(null)} className="text-slate-500">Cancel</Button>
              <Button
                data-testid="confirm-action-btn"
                onClick={submitAction}
                disabled={saving || (actionModal.type === 'clarification' && !actionComments.trim()) || (actionModal.target && !actionTarget)}
                className={`${actionModal.color} text-white hover:opacity-90`}
              >
                {saving ? 'Submitting...' : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ============ FACULTY DETAIL CARD ============
function FacultyDetailCard({ index, faculty, editing, onUpdate }) {
  const [expanded, setExpanded] = useState(index === 0);
  const f = faculty;
  // Show latest date first (descending)
  const dailyBreakdown = [...(f.daily_breakdown || [])].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const missing = f.missing_dates || [];
  const leaves = f.leave_dates || [];
  const reviewStats = f.review_stats;

  // Toggle critical flag on a line item; propagate change to parent via onUpdate(patch)
  const toggleCritical = (dayDate, section, itemIdx) => {
    if (!editing || !onUpdate) return;
    const newBreakdown = (f.daily_breakdown || []).map(d => {
      if (d.date !== dayDate) return d;
      const newItems = (d[section] || []).map((it, idx) => idx === itemIdx ? { ...it, critical: !it.critical } : it);
      return { ...d, [section]: newItems };
    });
    onUpdate({ daily_breakdown: newBreakdown });
  };

  const CritBox = ({ active }) => (
    <span
      className={`inline-flex items-center justify-center w-4 h-4 rounded border ${active ? 'bg-red-500 border-red-600 text-white' : 'bg-white border-slate-300'} cursor-pointer shrink-0`}
      title={active ? 'Marked critical' : 'Mark as critical'}
    >
      {active ? '✓' : ''}
    </span>
  );

  return (
    <div data-testid={`faculty-card-${index}`} className="border border-slate-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-slate-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-[260px]">
          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
            <UserIcon className="w-4.5 h-4.5 text-blue-700" />
          </div>
          <div>
            <p className="font-bold text-slate-800 text-sm">{f.faculty_name}</p>
            <p className="text-[11px] text-slate-500">{f.faculty_email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs flex-wrap">
          <Stat label="Reports" value={f.total_reports} />
          <Stat label="Academics" value={(f.duty_hours || 0).toFixed(1)} unit="h" color="text-blue-700" />
          <Stat label="Resp." value={(f.resp_hours || 0).toFixed(1)} unit="h" color="text-slate-700" />
          <Stat label="Init." value={(f.mktg_hours || 0).toFixed(1)} unit="h" color="text-emerald-700" />
          <Stat label="Leaves" value={leaves.length} color={leaves.length > 0 ? 'text-amber-700' : 'text-slate-400'} />
          <Stat label="Total" value={(f.total_hours || 0).toFixed(1)} unit="h" color="text-slate-900 font-bold" big />
        </div>
        <button
          data-testid={`toggle-faculty-${index}`}
          onClick={() => setExpanded(v => !v)}
          className="text-slate-500 hover:text-slate-800 px-2 py-1 rounded hover:bg-slate-100 flex items-center gap-1 text-xs font-semibold"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {expanded ? 'Hide details' : 'View details'}
        </button>
      </div>

      {/* Status row */}
      <div className="px-4 py-2 border-b border-slate-200 flex items-center gap-4 flex-wrap text-xs">
        {reviewStats && (
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[10px] uppercase font-bold tracking-wider text-indigo-500">Review activity:</span>
            <span className="text-slate-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded">
              Summaries generated: <b>{reviewStats.summaries_generated ?? 0}</b>
            </span>
            <span className="text-slate-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded">
              Forwarded: <b>{reviewStats.summaries_forwarded ?? 0}</b>
            </span>
            <span className="text-slate-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded">
              Avg fwd delay: <b>{reviewStats.avg_forward_delay_days ?? 0}d</b>
            </span>
            <span className="text-slate-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded">
              Reports reviewed: <b>{reviewStats.reports_reviewed ?? 0}</b>
            </span>
          </div>
        )}
        {leaves.length > 0 && (
          <span className="text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
            Leaves: {leaves.map(l => l.date).join(', ')}
          </span>
        )}
        {missing.length > 0 ? (
          <span className="text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded">
            Missing: {missing.join(', ')}
          </span>
        ) : (
          <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
            All days covered
          </span>
        )}
      </div>

      {/* Detailed breakdown */}
      {expanded && (
        <div className="bg-white p-4 space-y-3">
          {dailyBreakdown.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No reports submitted in this period.</p>
          ) : (
            dailyBreakdown.map((day, di) => {
              const rows = [
                ...(day.duties || []).map((d, i) => ({ ...d, _section: 'duties', _label: 'Academics', _color: 'text-blue-700 bg-blue-50', _idx: i, _detail: d.remarks })),
                ...(day.responsibilities || []).map((r, i) => ({ ...r, _section: 'responsibilities', _label: 'Responsibilities', _color: 'text-slate-700 bg-slate-100', _idx: i, _detail: [r.group_name, r.description].filter(Boolean).join(' · ') })),
                ...(day.course_marketings || []).map((m, i) => ({ ...m, _section: 'course_marketings', _label: 'Initiatives', _color: 'text-emerald-700 bg-emerald-50', _idx: i, _detail: m.description })),
              ].filter(x => x.topics || (x.hours || 0) > 0);
              return (
                <div key={day.date} data-testid={`day-detail-${index}-${di}`} className="border border-slate-200 rounded-md overflow-hidden">
                  <div className="bg-slate-50 px-3 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-700">{day.date}</span>
                      <span className="text-[10px] text-slate-400">({new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })})</span>
                    </div>
                    <span className="text-xs font-bold text-slate-700">{day.total_hours}h total</span>
                  </div>
                  {rows.length === 0 ? (
                    <p className="text-xs text-slate-400 italic px-3 py-2">No activity logged.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-slate-50/60 border-b border-slate-200">
                            {editing && <th className="w-8 py-1.5 pl-3"></th>}
                            <th className="text-left py-1.5 px-2 text-[9px] font-bold uppercase tracking-wider text-slate-400">Section</th>
                            <th className="text-left py-1.5 px-2 text-[9px] font-bold uppercase tracking-wider text-slate-400">Topic</th>
                            <th className="text-left py-1.5 px-2 text-[9px] font-bold uppercase tracking-wider text-slate-400">Details</th>
                            <th className="text-right py-1.5 px-2 text-[9px] font-bold uppercase tracking-wider text-slate-400">Hours</th>
                            <th className="text-left py-1.5 px-2 text-[9px] font-bold uppercase tracking-wider text-slate-400">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((it, i) => (
                            <tr
                              key={i}
                              data-testid={`activity-row-${index}-${day.date}-${it._section}-${it._idx}`}
                              onClick={() => toggleCritical(day.date, it._section, it._idx)}
                              className={`border-b border-slate-100 last:border-0 ${editing ? 'cursor-pointer hover:bg-red-50' : ''} ${it.critical ? 'bg-red-50' : ''}`}
                            >
                              {editing && <td className="pl-3 py-1.5 align-top"><CritBox active={!!it.critical} /></td>}
                              <td className="py-1.5 px-2 align-top whitespace-nowrap"><span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${it._color}`}>{it._label}</span></td>
                              <td className="py-1.5 px-2 align-top font-semibold text-slate-700">{it.topics || '—'}</td>
                              <td className="py-1.5 px-2 align-top text-slate-500">{it._detail || '—'}</td>
                              <td className="py-1.5 px-2 align-top text-right text-slate-700 font-semibold whitespace-nowrap">{it.hours}h</td>
                              <td className="py-1.5 px-2 align-top whitespace-nowrap">
                                <span className="text-slate-600">{it.status || '—'}</span>
                                {it.critical && !editing && <span className="ml-1.5 text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-red-100 text-red-700">Critical</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* CIC note inline editor */}
          <div className="pt-2 border-t border-slate-100">
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-400">CIC Note (this faculty)</Label>
            {editing ? (
              <textarea
                data-testid={`edit-note-${index}`}
                value={f.cic_note || ''}
                onChange={(e) => onUpdate({ cic_note: e.target.value })}
                rows={2}
                className="w-full mt-1 border border-slate-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Add CIC observations for this faculty..."
              />
            ) : (
              <p className="text-xs text-slate-600 mt-1">{f.cic_note || <span className="text-slate-400 italic">No note added.</span>}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, unit = '', color = 'text-slate-700', big }) {
  return (
    <div className="flex flex-col items-end leading-tight">
      <span className="text-[9px] uppercase tracking-wider font-bold text-slate-400">{label}</span>
      <span className={`${color} ${big ? 'text-base font-bold' : 'text-sm font-semibold'}`}>{value}{unit}</span>
    </div>
  );
}
